import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../../utils/agent.store'
import {
  addToaster,
  clearFormState,
  getGenericEntityListingAPI,
  getPageDataByPage,
  getPagesByBatchId,
  getProgramById,
  getTemplateByBatchId,
  hideLoader,
  ILayoutTemplatePageRenderer,
  ILayoutTemplatePageRendererRef,
  IOCRFieldMetaState,
  IOCRFieldState,
  ITabsPageResponse,
  ITabsResponse,
  ITemplateLayoutMetaType,
  showLoader,
  submitReviewPageData,
  permissionToProceedManually,
  updateFieldMetaState,
  updateFormState,
  updateTemplateLayout,
  useTenantNavigate,
} from '@app-store'
import {
  componentNames,
  LAYOUT_TEMPLATE_SUB_SECTION_MAPPING,
  NAMESPACES,
  OCR_CASE_CREATION_FAILED_MESSAGE,
  OCR_CONFIDENCE_THRESHOLD,
  OCR_PAGE_REVIEW_STATUS,
  OCR_PAGE_STATUS,
  OCR_REJECTED_TAB_KEY_PREFIX,
  OCR_COMMUNICATION_STATUS,
  OCR_STATUS,
  OCR_TAB_IDENTIFIER,
  OcrStatus,
  PERMISSION_MODULES,
  PERMISSION_ACCESSIBILITY,
} from '../../../utils/constants'
import OCRPageGlobalHeader from './OCRPageGlobalHeader'
import {
  Accordion,
  communicationStatus,
  CustomButton,
  Modal,
  OnTabChangeCallbackAttributes,
  StepperBV,
  SvgOcrRejectedPage,
  SvgOcrMultiFormsDetected,
  SvgOcrFailed,
  SvgOcrProcessing,
  SvgErrorIcon,
  SvgWarning,
  TabSplits,
  TabSplitsTypes,
} from '@ui-kit'
import OCRTemplateElementRenderer from './OCRTemplateElementRenderer'
import OCRRejectedPage from './OCRRejectedPage'
import {
  FieldErrors,
  FieldValues,
  FormProvider,
  useForm,
} from 'react-hook-form'
import {
  formatPhoneNumber,
  permissionObject,
} from '../../../utils/helperFunctions'
import { useParams } from 'react-router-dom'
import { editEntityDataEmbeddedAPI } from '../../../apis/entityData.api'
import { QUEUE_STRINGS } from '../../../utils/queueIntake.types'
import { AxiosResponse } from 'axios'
import { isEmpty, uniqueId } from 'lodash'
import moment from 'moment'
import { STRINGS } from '../../../utils/strings'
import {
  LayoutTemplateRowRendererProps,
  LayoutTemplateSectionRendererProps,
  LayoutTemplateSubSectionRendererProps,
} from '../../../types/ocr.types'

export const LayoutTemplateRowRenderer: React.FC<
  LayoutTemplateRowRendererProps
> = ({ row, ocrConfidenceThreshold = OCR_CONFIDENCE_THRESHOLD }) => {
  const layout = useAppSelector((state) => state?.ocrInfoSlice?.templateLayout)
  return (
    <div className="grid grid-cols-3 w-full gap-2">
      {row?.elements?.map((element) => {
        const uiDataType =
          layout?.added_fields?.[element?.key ?? '']?.ui_data_type
        const isFullWidthField =
          uiDataType === componentNames.CONSENTS ||
          uiDataType === componentNames.MULTI_SELECT_DROPDOWN

        return (
          <div
            key={element?.key}
            className={`min-w-0 ${
              isFullWidthField ? 'col-span-3' : 'col-span-1'
            } pt-3 px-3`}
          >
            <OCRTemplateElementRenderer
              element={element}
              elementMeta={
                layout?.added_fields?.[element?.key?.toString() ?? '']
              }
              ocrConfidenceThreshold={ocrConfidenceThreshold}
            />
          </div>
        )
      })}
    </div>
  )
}

export const LayoutTemplateSubSectionRenderer: React.FC<
  LayoutTemplateSubSectionRendererProps
> = ({ subSection, ocrConfidenceThreshold = OCR_CONFIDENCE_THRESHOLD }) => {
  return (
    // <div
    //   className={` ${
    //     subSection?.type === LAYOUT_TEMPLATE_SUB_SECTION_MAPPING.NO_HEADING
    //       ? ''
    //       : 'border border-accent-100  m-2 rounded-sm'
    //   }`}
    // >
    //   {subSection?.type ===
    //   LAYOUT_TEMPLATE_SUB_SECTION_MAPPING.NO_HEADING ? null : (
    //     <div className="bg-accent-50 text-sm font-normal pl-3 py-2 border-b border-accent-100">
    //       <span>{subSection?.label}</span>
    //     </div>
    //   )}
    <div className="flex flex-col gap-1">
      {subSection?.rows?.map((row, index) => (
        <div
          key={`${subSection.key ?? subSection.label ?? 'row'}-${index}`}
          className={`${
            index === subSection?.rows?.length - 1 ? 'py-2' : 'pt-2'
          }`}
        >
          <LayoutTemplateRowRenderer
            row={row}
            ocrConfidenceThreshold={ocrConfidenceThreshold}
          />
        </div>
      ))}
    </div>
    // </div>
  )
}

export const LayoutTemplateSectionRenderer: React.FC<
  LayoutTemplateSectionRendererProps
> = ({ section, ocrConfidenceThreshold = OCR_CONFIDENCE_THRESHOLD }) => {
  switch (section?.type) {
    case LAYOUT_TEMPLATE_SUB_SECTION_MAPPING.LAYOUT:
      return (
        <Accordion
          heading={
            section?.label ? (
              <span className="text-sm font-medium">{section?.label}</span>
            ) : null
          }
          alwaysOpen
        >
          <div className="flex flex-col gap-2">
            {section?.sub_sections?.map((subSection) => (
              <LayoutTemplateSubSectionRenderer
                key={subSection.key ?? subSection.label}
                subSection={subSection}
                ocrConfidenceThreshold={ocrConfidenceThreshold}
              />
            ))}
          </div>
        </Accordion>
      )
    case LAYOUT_TEMPLATE_SUB_SECTION_MAPPING?.NO_HEADING:
      return (
        <div className="flex flex-col gap-2">
          {section?.sub_sections?.map((subSection) => (
            <LayoutTemplateSubSectionRenderer
              key={subSection.key ?? subSection.label}
              subSection={subSection}
              ocrConfidenceThreshold={ocrConfidenceThreshold}
            />
          ))}
        </div>
      )
    default:
      return (
        <Accordion
          heading={
            section?.label ? (
              <span className="text-sm font-medium">{section?.label}</span>
            ) : null
          }
          alwaysOpen
        >
          <div className="flex flex-col gap-2">
            {section?.sub_sections?.map((subSection) => (
              <LayoutTemplateSubSectionRenderer
                key={subSection.key ?? subSection.label}
                subSection={subSection}
                ocrConfidenceThreshold={ocrConfidenceThreshold}
              />
            ))}
          </div>
        </Accordion>
      )
  }
}

export const LayoutTemplatePageRenderer = forwardRef<
  ILayoutTemplatePageRendererRef,
  ILayoutTemplatePageRenderer
>(
  (
    {
      pageKey,
      faxPageNumber,
      setCurrStep,
      lastPage,
      batchId,
      setFinalInstanceData,
      pageReviewStatus,
      ocrConfidenceThreshold = OCR_CONFIDENCE_THRESHOLD,
    },
    ref
  ) => {
    const dispatch = useAppDispatch()
    const methods = useForm({ reValidateMode: 'onChange', mode: 'onChange' })
    const [selectedPageData, setSelectedPageData] = useState<FieldValues>()
    const template = useAppSelector(
      (state) => state?.ocrInfoSlice?.templateLayout
    )
    const addedFields = template?.added_fields as Record<
      string,
      ITemplateLayoutMetaType
    >
    const categories = useAppSelector(
      (state) => state?.categoryDropdown?.categoryMap
    )
    const currentPage = template?.layout_data?.pages?.find(
      (page) => page?.key === pageKey
    )
    const ocrStateRef = useAppSelector(
      (state) => state?.ocrInfoSlice?.formState
    )
    const fieldMeta = useAppSelector(
      (state) => state?.ocrInfoSlice?.fieldMetaState
    )

    const {
      reset,
      handleSubmit,
      getValues,
      formState: { isDirty },
    } = methods
    useImperativeHandle(ref, () => ({
      handleDraft: () => getValues(),
      handleSubmit: () => {
        handleSubmit(onFormSubmit, onValidationErrors)()
      },
      isDirty: isDirty,
    }))

    const normalizeOcrRawValue = (value: unknown): string => {
      const raw = String(value ?? '').trim()
      if (!raw || raw === '--blank--') return ''
      if (raw === '--signed--') return 'Signed'
      return raw
    }

    const preProcessFormData = (
      data: Record<string, IOCRFieldState>
    ): FieldValues => {
      const processedData: FieldValues = {}
      const fieldMetaState: Record<string, IOCRFieldMetaState> = {}

      Object.keys(data ?? {})?.forEach((key) => {
        const fieldData = data?.[key]
        const { original_value, ...metaData } = fieldData
        const normalizedValue = normalizeOcrRawValue(original_value)
        const uiDataType = addedFields?.[key]?.ui_data_type

        if (uiDataType === componentNames?.CATEGORY) {
          if (!normalizedValue) {
            processedData[key] = { id: '' }
          } else {
            const selectedCategory =
              categories?.[addedFields?.[key ?? '']?.category_key ?? '']
            const selectedCategoryOption = selectedCategory?.find((val) =>
              val?.value?.startsWith(normalizedValue)
            )
            processedData[key] = { id: selectedCategoryOption?.id ?? '' }
          }
        } else if (uiDataType === componentNames?.PHONE_NUMBER) {
          processedData[key] = normalizedValue
            ? formatPhoneNumber(normalizedValue)
            : ''
        } else if (uiDataType === componentNames?.CONSENTS) {
          const lower = normalizedValue.toLowerCase()
          if (!normalizedValue || lower === 'no') {
            processedData[key] = false
          } else if (
            lower === 'yes' ||
            lower === 'true' ||
            lower === 'signed'
          ) {
            processedData[key] = true
          } else {
            processedData[key] = normalizedValue
          }
        } else {
          processedData[key] = normalizedValue
        }

        fieldMetaState[key] = { ...metaData }
      })

      dispatch(updateFieldMetaState({ ...fieldMeta, ...fieldMetaState }))
      return processedData
    }

    const populateFormData = (data: FieldValues) => {
      if (data) {
        reset(data)
        const currentPageInfo = ocrStateRef?.[pageKey]
        if (isEmpty(currentPageInfo)) {
          dispatch(
            updateFormState({
              pageKey,
              pageData: {
                pageData: data,
                pageStatus: OCR_PAGE_STATUS.PENDING,
                errorCount: 0,
              },
            })
          )
        } else {
          dispatch(
            updateFormState({
              pageKey,
              pageData: {
                ...currentPageInfo,
                pageData: data,
              },
            })
          )
        }
      }
    }

    const createDataObject = (data: FieldValues[]) => {
      const combined: FieldValues = {}

      data.forEach((element: FieldValues) => {
        const key = element?.key
        combined[key] = {
          confidence: element?.confidence,
          validated: element?.validated,
          is_valid: element?.is_valid,
          validation_description: element?.validation_description,
          validation_message: element?.validation_message,
          page_index: element?.page_index,
          page_number: element?.page_number,
          assigned_template: element?.assigned_template,
          original_value: element?.value,
        }
      })

      return combined
    }

    const createDataArray = (
      data: FieldValues,
      fieldMeta: Record<string, IOCRFieldMetaState>
    ) => {
      const combined: FieldValues[] = []

      Object.keys(data).forEach((key) => {
        const meta = fieldMeta[key]
        const uiDataType = addedFields?.[key]?.ui_data_type
        let value = data[key]

        if (uiDataType === componentNames?.CONSENTS) {
          if (typeof value === 'boolean') {
            value = value ? 'Yes' : 'No'
          } else if (!value) {
            value = 'No'
          }
        } else if (uiDataType === componentNames?.DATE && value) {
          const parsedDate = moment(
            value,
            ['YYYY-MM-DD', 'MM/DD/YYYY', 'DD/MM/YYYY'],
            true
          )
          if (parsedDate.isValid()) {
            value = parsedDate.format('DD/MM/YYYY')
          }
        }

        combined.push({
          key: key,
          value: value,
          confidence: meta?.confidence,
          validated: meta?.validated,
          is_valid: meta?.is_valid,
          validation_description: meta?.validation_description,
          validation_message: meta?.validation_message,
          page_index: meta?.page_index,
          page_number: meta?.page_number,
          assigned_template: meta?.assigned_template,
        })
      })

      return combined
    }
    const onFormSubmit = async (data: FieldValues) => {
      try {
        dispatch(showLoader())
        const finalData: FieldValues = {
          approved_data: {
            data: createDataArray(data, fieldMeta ?? {}),
          },
          page_number: faxPageNumber,
          batch_id: batchId,
          final_flag: false,
        }
        const res: FieldValues = await submitReviewPageData(
          batchId,
          faxPageNumber,
          finalData
        )

        if (res?.data) {
          if (lastPage) {
            setFinalInstanceData?.({
              approved_data: {
                data: createDataArray(data, fieldMeta ?? {}),
              },
            })
          }
          // Reset form to clear dirty state
          reset(data, { keepValues: true })
          dispatch(
            updateFormState({
              pageKey: pageKey,
              pageData: {
                pageData: data,
                pageStatus: OCR_PAGE_STATUS.COMPLETED,
                isDirty: false,
              },
            })
          )
          dispatch(
            addToaster({
              id: uniqueId(),
              type: 'success',
              message: NAMESPACES.OCR_DATA_SAVED_SUCCESS,
            })
          )
          if (!lastPage) {
            setCurrStep((prev) => prev + 1)
          }
        }
      } catch (err) {
        console.error(err)
        dispatch(
          addToaster({
            id: uniqueId(),
            type: 'error',
            message: NAMESPACES.OCR_SAVE_PAGE_FAILED,
          })
        )
      } finally {
        dispatch(hideLoader())
      }
    }

    const onValidationErrors = (errors: FieldErrors) => {
      const clondedData = getValues()
      dispatch(
        updateFormState({
          pageKey: pageKey,
          pageData: {
            pageData: structuredClone(clondedData),
            pageStatus: OCR_PAGE_STATUS.ERRORS,
            errorCount: Object.keys(errors).length,
          },
        })
      )
    }

    const categoriesLoaded = categories && Object.keys(categories).length > 0

    useEffect(() => {
      if (!categoriesLoaded || !pageKey) return

      const currentPageInfo = ocrStateRef?.[pageKey]
      if (currentPageInfo?.pageData && !isEmpty(currentPageInfo.pageData)) {
        reset(currentPageInfo.pageData)
        return
      }

      if (!selectedPageData || isEmpty(selectedPageData)) return

      const processedData = preProcessFormData(
        selectedPageData as Record<string, IOCRFieldState>
      )
      populateFormData(processedData)
    }, [categoriesLoaded, pageKey, selectedPageData])

    useEffect(() => {
      if (!batchId || !pageKey) return
      setSelectedPageData(undefined)
      let cancelled = false

      const load = async () => {
        dispatch(showLoader())
        try {
          const res = await getPageDataByPage(batchId, faxPageNumber)
          if (cancelled) return
          if (res?.data) {
            const record = res?.data?.[0]
            setSelectedPageData(
              createDataObject(
                record?.approved_data?.data ??
                  record?.approved_data?.inbound_json?.data
              )
            )
          }
        } catch (err) {
          console.error(err)
        } finally {
          if (!cancelled) dispatch(hideLoader())
        }
      }

      load()
      return () => {
        cancelled = true
      }
    }, [batchId, faxPageNumber, pageKey])

    // Sync isDirty changes to Redux so parent useEffect can react
    useEffect(() => {
      const currentPageInfo = ocrStateRef?.[pageKey]
      if (currentPageInfo && currentPageInfo.isDirty !== isDirty) {
        dispatch(
          updateFormState({
            pageKey,
            pageData: {
              ...currentPageInfo,
              isDirty: isDirty,
            },
          })
        )
      }
    }, [isDirty, pageKey, ocrStateRef, dispatch])

    return (
      <FormProvider {...methods}>
        <form className="flex h-full min-h-0 flex-col">
          <button className="absolute hidden">{NAMESPACES.SUBMIT}</button>
          <button className="absolute hidden">{NAMESPACES.DRAFT}</button>
          <div className="shrink-0 bg-white pt-3 pb-2 border-b border-secondary-100">
            <OCRPageGlobalHeader pageReviewStatus={pageReviewStatus} />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {currentPage?.sections?.map((section) => (
              <LayoutTemplateSectionRenderer
                key={section.key}
                section={section}
                ocrConfidenceThreshold={ocrConfidenceThreshold}
              />
            ))}
          </div>
        </form>
      </FormProvider>
    )
  }
)

LayoutTemplatePageRenderer.displayName = 'LayoutTemplatePageRenderer'

export const TabHeaderPageSummary: React.FC<{
  acceptedCount: number
  rejectedCount: number
}> = ({ acceptedCount, rejectedCount }) => {
  return (
    <div className="flex flex-col flex-shrink-0 whitespace-nowrap p-2 justify-between items-center bg-secondary-25 border-r-2 border-secondary-500 mr-1">
      <span className="text-xs font-medium px-1">
        {NAMESPACES.OCR_ACCEPTED_LABEL}: {acceptedCount}
      </span>
      <hr className="w-full bg-accent-900 border-0 my-1 h-[0.05rem]" />
      <span className="text-xs font-medium px-1">
        {NAMESPACES.OCR_REJECTED_LABEL}: {rejectedCount}
      </span>
    </div>
  )
}

const getLastAssignedTemplatePage = (
  pages: ITabsPageResponse[] | undefined
): ITabsPageResponse | undefined => {
  if (!pages?.length) return undefined
  for (let i = pages.length - 1; i >= 0; i--) {
    if (pages[i]?.assigned_template) return pages[i]
  }
  return undefined
}

const OCRReviewPage = ({
  setRefetch,
  programId,
}: {
  setRefetch: (refetch: number) => void
  programId?: string
}) => {
  const { communicationId } = useParams()
  const dispatch = useAppDispatch()
  const templateJSON = useAppSelector(
    (state) => state?.ocrInfoSlice?.templateLayout
  )
  const communication_status = useAppSelector(
    (state) =>
      state.categoryDropdown?.categoryMap?.[STRINGS.COMMUNICATION_STATUS]
  )
  const ocrStateRef = useAppSelector((state) => state?.ocrInfoSlice?.formState)
  const registryRef = useRef(new Map<string, ILayoutTemplatePageRendererRef>())
  const [currStep, setCurrStep] = useState<number>(0)
  const [finalInstanceData, setFinalInstanceData] = useState<FieldValues>()
  const [activeTabId, setActiveTabId] = useState<string>('')
  const [tabList, setTabList] = useState<TabSplitsTypes[]>([])
  const [batchId, setBatchId] = useState<string | null>(null)
  const [ocrConfidenceThreshold, setOcrConfidenceThreshold] = useState<number>(
    OCR_CONFIDENCE_THRESHOLD
  )
  const [ocrStatus, setOcrStatus] = useState<OcrStatus>(
    OCR_STATUS.READY_FOR_REVIEW
  )
  const [rejectionReason, setRejectionReason] = useState<string | null>(null)
  const navigate = useTenantNavigate()
  const [tabsMetaInfo, setTabsMetaInfo] = useState<ITabsResponse | null>(null)
  const [nextButtonEnabled, setNextButtonEnabled] = useState<boolean>(false)
  const [manualOverrideEnabledAt, setManualOverrideEnabledAt] = useState<
    string | null
  >(null)
  const [showProceedButton, setShowProceedButton] = useState<boolean>(false)
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false)
  const [isSaveButtonEnabled, setIsSaveButtonEnabled] = useState<boolean>(false)

  const hasOcrManualReviewPermission = useAppSelector((state: RootState) => {
    const permissions = state?.clientPermissions?.programPermissions
    const progId =
      state?.clientPrograms?.selectedClientPrograms?.program_id?.toString()

    if (!permissions || !progId) return false

    const result = permissionObject.Manage_Dashboard(
      permissions,
      PERMISSION_MODULES.OCR_MANUAL_REVIEW,
      PERMISSION_ACCESSIBILITY.MANAGE,
      progId
    )

    return result
  })

  const fetchProgramOcrThreshold = async (progId: string) => {
    try {
      const response = await getProgramById({ id: Number(progId) })
      if (response?.data?.ocr_confidence_threshold) {
        setOcrConfidenceThreshold(response.data.ocr_confidence_threshold)
      }
    } catch (err) {
      console.error('[OCR Review] fetchProgramOcrThreshold failed:', err)
    }
  }

  const syncActivePageDraftToRedux = useCallback(() => {
    const currentPage = tabsMetaInfo?.pages?.[currStep]
    if (!currentPage?.assigned_template || !activeTabId) return

    const pageKey = `${currentPage.assigned_template}${currentPage.sheet_number}`

    const pageRenderer = registryRef.current.get(activeTabId)
    if (!pageRenderer) return

    const draftData = pageRenderer.handleDraft()
    if (isEmpty(draftData)) return

    const existingPage = ocrStateRef?.[pageKey]

    dispatch(
      updateFormState({
        pageKey,
        pageData: {
          pageData: structuredClone(draftData),
          pageStatus: existingPage?.pageStatus ?? OCR_PAGE_STATUS.PENDING,
          errorCount: existingPage?.errorCount ?? 0,
          isDirty: pageRenderer.isDirty,
        },
      })
    )
  }, [activeTabId, currStep, dispatch, ocrStateRef, tabsMetaInfo])

  const onTabChange = (tab: OnTabChangeCallbackAttributes) => {
    syncActivePageDraftToRedux()
    setCurrStep(Number(tab.index))
  }

  const registerPage = useCallback((key: string) => {
    return (instance: ILayoutTemplatePageRendererRef | null) => {
      if (instance) registryRef.current.set(key, instance)
      else registryRef.current.delete(key)
    }
  }, [])

  const handleSaveAndNext = () => {
    if (!activeTabId) return

    registryRef.current.get(activeTabId)?.handleSubmit()
  }

  const updateCommunicationFaxStatus = async (
    statusLabel: OcrStatus | string
  ) => {
    const statusId = communication_status?.find(
      (status) => status?.label === statusLabel || status?.value === statusLabel
    )?.id
    if (!statusId) {
      throw new Error(`Communication status not found: ${statusLabel}`)
    }

    await editEntityDataEmbeddedAPI(
      QUEUE_STRINGS?.COMMUNICATION_LOWERCASE,
      communicationId ?? '',
      { category_status: { id: statusId, _state: 'CHANGED' } },
      ['category_status']
    )
    if ((Object.values(OCR_STATUS) as string[]).includes(statusLabel)) {
      setOcrStatus(statusLabel as OcrStatus)
    }
  }

  const handleProceedManually = async (options?: { disableOcr?: boolean }) => {
    if (!hasOcrManualReviewPermission) {
      dispatch(
        addToaster({
          id: uniqueId(),
          type: 'error',
          message: 'You do not have permission to proceed Manually',
        })
      )
      return
    }

    try {
      dispatch(showLoader())
      if (options?.disableOcr) {
        await permissionToProceedManually(
          communicationId ?? '',
          programId,
          OCR_COMMUNICATION_STATUS.RESULTS_DELAYED
        )
      }
      const nextStatus =
        ocrStatus === OCR_STATUS.CASE_CREATION_FAILED ||
        ocrStatus === OCR_STATUS.FAILED ||
        ocrStatus === OCR_STATUS.REJECTED
          ? communicationStatus.PROCESSING
          : OCR_STATUS.RESULTS_DELAYED
      await updateCommunicationFaxStatus(nextStatus)
      setRefetch(Date.now())
    } catch (err) {
      console.error(err)
      dispatch(
        addToaster({
          id: uniqueId(),
          type: 'error',
          message: NAMESPACES.OCR_PROCEED_INTAKE_FAILED,
        })
      )
    } finally {
      dispatch(hideLoader())
    }
  }

  const openConfirmationModal = () => {
    if (!hasOcrManualReviewPermission) {
      dispatch(
        addToaster({
          id: uniqueId(),
          type: 'error',
          message: 'You do not have permission to perform this action',
        })
      )
      return
    }
    setShowConfirmModal(true)
  }

  const onConfirmOverride = () => {
    setShowConfirmModal(false)
    handleProceedManually({ disableOcr: true })
  }
  const updateCaseStatusToFailed = async () => {
    try {
      dispatch(showLoader())
      await updateCommunicationFaxStatus(OCR_STATUS.CASE_CREATION_FAILED)
    } catch (err) {
      console.error(err)
      dispatch(
        addToaster({
          id: uniqueId(),
          type: 'error',
          message: NAMESPACES.OCR_CASE_CREATION_FAILED_MESSAGE,
        })
      )
    } finally {
      dispatch(hideLoader())
    }
  }
  const submitFinalReviewAndCreateCase = async () => {
    const lastAssignedTemplatePage = getLastAssignedTemplatePage(
      tabsMetaInfo?.pages
    )
    const lastPageNumber = Number(lastAssignedTemplatePage?.page_number)
    if (!batchId || !lastPageNumber) return

    try {
      dispatch(showLoader())
      const finalData: FieldValues = {
        page_number: lastPageNumber,
        batch_id: batchId,
        final_flag: true,
        approved_data: {
          data: finalInstanceData?.approved_data?.data,
        },
      }
      const res: FieldValues = await submitReviewPageData(
        batchId,
        lastPageNumber,
        finalData
      )
      const caseId = res?.data?.case_id
      if (caseId) {
        const params = new URLSearchParams(window.location.search)
        params.set('caseId', caseId.toString())
        navigate(`${window.location.pathname}?${params.toString()}`)
      } else {
        dispatch(
          addToaster({
            id: uniqueId(),
            type: 'error',
            message: OCR_CASE_CREATION_FAILED_MESSAGE,
          })
        )
      }
    } catch (err) {
      console.error(err)
      updateCaseStatusToFailed()
      dispatch(
        addToaster({
          id: uniqueId(),
          type: 'error',
          message: OCR_CASE_CREATION_FAILED_MESSAGE,
        })
      )
    } finally {
      dispatch(hideLoader())
    }
  }
  const updateTabs = () => {
    const tabs: TabSplitsTypes[] = []
    const lastAssignedTemplatePage = getLastAssignedTemplatePage(
      tabsMetaInfo?.pages
    )
    tabsMetaInfo?.pages?.forEach((page, index) => {
      const isRejected = !page?.assigned_template
      const tabLabel = `TP ${page?.sheet_number ?? '--'} / FP ${index + 1}`

      tabs?.push({
        label: (
          <span className="inline-flex items-center gap-1.5">
            {tabLabel}
            {isRejected && (
              <SvgErrorIcon className="flex-shrink-0" aria-hidden />
            )}
          </span>
        ),
        key: page?.assigned_template
          ? `${page.assigned_template}${page.sheet_number}`
          : `${OCR_REJECTED_TAB_KEY_PREFIX}${page?.page_number}`,
        tooltipText: NAMESPACES.OCR_TAB_TOOLTIP,
        identifier: page?.assigned_template
          ? OCR_TAB_IDENTIFIER.ACCEPTED
          : OCR_TAB_IDENTIFIER.REJECTED,
        content: page?.assigned_template ? (
          <LayoutTemplatePageRenderer
            setCurrStep={setCurrStep}
            pageKey={`${page.assigned_template}${page.sheet_number}`}
            faxPageNumber={Number(page?.page_number)}
            lastPage={page === lastAssignedTemplatePage}
            batchId={batchId ?? ''}
            ref={registerPage(`${page.assigned_template}${page.sheet_number}`)}
            setFinalInstanceData={setFinalInstanceData}
            pageReviewStatus={OCR_PAGE_REVIEW_STATUS.ACCEPTED}
            ocrConfidenceThreshold={ocrConfidenceThreshold}
          />
        ) : (
          <div className="flex h-full min-h-0 flex-col">
            <div className="shrink-0 bg-white pt-3 pb-2 border-b border-secondary-100">
              <OCRPageGlobalHeader
                pageReviewStatus={OCR_PAGE_REVIEW_STATUS.REJECTED}
              />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto h-96 w-full flex items-center justify-center bg-secondary-25 p-4 rounded flex-col gap-6">
              <div className="h-full w-full flex flex-col items-center justify-center gap-3">
                <SvgOcrRejectedPage />
                <span className="font-normal text-lg text-center">
                  {NAMESPACES.OCR_REJECTED_PAGE}
                </span>
              </div>
            </div>
          </div>
        ),
      })
    })
    setTabList(tabs)
  }

  const fetchCommunicationOcrMeta = async () => {
    try {
      const payload = {
        select: ['category_status', 'ocr_outbound_batch', 'reason'],
        criteria: [],
        related_entities: {
          ocr_outbound_batch: {
            select: ['batch_id', 'manual_override_enabled_at'],
            criteria: [],
          },
        },
        entity_name: 'communication',
      }
      const res = await getGenericEntityListingAPI(payload, communicationId)
      if (res?.ocr_outbound_batch?.batch_id) {
        setBatchId(res.ocr_outbound_batch.batch_id.toString())
      }
      if (res?.category_status) {
        setOcrStatus(res?.category_status?.label)
      }
      if (typeof res?.reason === 'string' && res.reason.trim()) {
        setRejectionReason(res.reason.trim())
      } else {
        setRejectionReason(null)
      }

      const manualOverrideAt =
        res?.ocr_outbound_batch?.manual_override_enabled_at

      if (manualOverrideAt) {
        setManualOverrideEnabledAt(manualOverrideAt)
      }

      fetchEntireTemplate(res?.ocr_outbound_batch?.batch_id)
      fetchTabsInfo(res?.ocr_outbound_batch?.batch_id)
    } catch (err) {
      console.error('[OCR Review] fetchCommunicationOcrMeta failed:', err)
    }
  }

  const fetchEntireTemplate = async (batchIdprop: string) => {
    try {
      const res = await getTemplateByBatchId(batchIdprop ?? batchId)
      if (res?.data) {
        dispatch(updateTemplateLayout(res?.data?.layout_json))
      }
    } catch (err) {
      console.error(err)
    }
  }

  const fetchTabsInfo = async (batchIdprop: string) => {
    try {
      const res: AxiosResponse<ITabsResponse> = await getPagesByBatchId(
        batchIdprop ?? batchId
      )
      if (res?.data?.pages) {
        setTabsMetaInfo(res?.data)
      }
      setActiveTabId(
        res?.data?.pages?.[0]?.assigned_template
          ? `${res.data.pages[0].assigned_template}${res.data.pages[0].sheet_number}`
          : ''
      )
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    if (programId) {
      fetchProgramOcrThreshold(programId)
    }
  }, [programId])

  useEffect(() => {
    if (tabList?.length != tabsMetaInfo?.pages?.length && templateJSON)
      updateTabs()
  }, [tabsMetaInfo, templateJSON, batchId, currStep])

  useEffect(() => {
    const page = tabsMetaInfo?.pages?.[currStep]
    setActiveTabId(
      page?.assigned_template
        ? `${page.assigned_template}${page.sheet_number}`
        : page
        ? `rejected-${page.page_number}`
        : ''
    )
  }, [currStep, tabsMetaInfo])

  useEffect(() => {
    if (ocrStatus === OCR_STATUS.PROCESSING_FAILED) {
      setNextButtonEnabled(true)
      return
    }

    const pageKeys = Object.keys(ocrStateRef ?? {})
    const allPagesCompletedAndFilled = pageKeys.every((key) => {
      const page = ocrStateRef?.[key]

      return (
        page?.pageStatus === OCR_PAGE_STATUS.COMPLETED &&
        (page?.errorCount ?? 0) === 0
      )
    })

    const allPagesVisited =
      pageKeys.length +
        (tabsMetaInfo?.rejected_pages?.page_indexes?.length ?? 0) ===
      tabList?.length

    setNextButtonEnabled(allPagesCompletedAndFilled && allPagesVisited)
  }, [ocrStateRef, tabList, tabsMetaInfo, ocrStatus])

  useEffect(() => {
    if (
      ocrStatus === OCR_STATUS.PROCESSING_FAILED &&
      tabsMetaInfo?.pages?.length
    ) {
      setCurrStep(tabsMetaInfo.pages.length - 1)
    }
  }, [ocrStatus, tabsMetaInfo])

  useEffect(() => {
    fetchCommunicationOcrMeta()
    return () => {
      dispatch(clearFormState())
    }
  }, [communicationId])

  useEffect(() => {
    if (ocrStatus !== OCR_STATUS.IN_PROGRESS || !manualOverrideEnabledAt) {
      setShowProceedButton(false)
      return
    }

    const allowedAt = new Date(manualOverrideEnabledAt).getTime()
    const now = Date.now()
    const isAllowed = now > allowedAt

    setShowProceedButton(isAllowed)
  }, [ocrStatus, manualOverrideEnabledAt])

  useEffect(() => {
    if (!activeTabId) {
      setIsSaveButtonEnabled(true)
      return
    }

    const pageRenderer = registryRef.current.get(activeTabId)
    if (!pageRenderer) {
      setIsSaveButtonEnabled(true)
      return
    }

    const currentPage = tabsMetaInfo?.pages?.[currStep]
    if (!currentPage?.assigned_template) {
      setIsSaveButtonEnabled(false)
      return
    }

    const pageKey = `${currentPage.assigned_template}${currentPage.sheet_number}`
    const pageState = ocrStateRef?.[pageKey]

    // Check if page has been saved before
    const hasBeenSaved = pageState?.pageStatus === OCR_PAGE_STATUS.COMPLETED

    if (hasBeenSaved) {
      setIsSaveButtonEnabled(
        Boolean(pageRenderer.isDirty || pageState?.isDirty)
      )
    } else {
      setIsSaveButtonEnabled(true)
    }
  }, [activeTabId, currStep, tabsMetaInfo, ocrStateRef])

  if (ocrStatus === OCR_STATUS.IN_PROGRESS) {
    return (
      <div className="flex h-full w-full flex-col items-center p-4">
        <div className="flex flex-col items-center gap-4 mt-40">
          <SvgOcrProcessing />

          <div className="text-center w-[18.1rem] flex flex-col items-center justify-center gap-2 font-primary">
            <div className="text-base font-bold text-black">
              {NAMESPACES.OCR_PROCESSING_TITLE}
            </div>
            <div className="text-base text-black">
              {NAMESPACES.OCR_PROCESSING_MESSAGE}
            </div>
          </div>
        </div>

        {showProceedButton && (
          <>
            <div className="mt-8">
              <CustomButton
                isFilled
                color="others"
                className="!bg-blue-600 text-white hover:!bg-blue-700 w-44 h-8"
                onClick={openConfirmationModal}
                disabled={!hasOcrManualReviewPermission}
              >
                OCR Manual Override
              </CustomButton>
            </div>

            <Modal
              isOpen={showConfirmModal}
              closeModal={() => setShowConfirmModal(false)}
              showCloseButton={true}
              width="w-[616px]"
              bodyCssClassName="!p-3 border border-accent-200 rounded-2xl"
            >
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-center gap-4 px-4 py-6 border-b border-accent-100">
                  <div className="bg-red-100 rounded-full h-[3.625rem] w-[3.625rem] flex items-center justify-center min-w-[3.625rem]">
                    <SvgWarning />
                  </div>
                  <span className="text-center font-normal text-lg leading-[22px] text-[#171717]">
                    You are about to override the OCR process for this fax.
                  </span>
                </div>
                <div className="flex gap-3 items-center justify-end w-full">
                  <CustomButton
                    onClick={() => setShowConfirmModal(false)}
                    color="primary"
                    className="border border-primary-500 text-xs !w-fit"
                  >
                    <span className="text-xs text-accent-900 font-light">
                      Cancel
                    </span>
                  </CustomButton>
                  <CustomButton
                    onClick={onConfirmOverride}
                    color="primary"
                    isFilled={true}
                    className="w-fit"
                  >
                    Confirm
                  </CustomButton>
                </div>
              </div>
            </Modal>
          </>
        )}
      </div>
    )
  } else if (ocrStatus === OCR_STATUS.REJECTED) {
    return (
      <OCRRejectedPage
        onProceedManually={() => handleProceedManually()}
        rejectionReason={rejectionReason}
      />
    )
  } else if (ocrStatus === OCR_STATUS.FAILED) {
    return (
      <div className="flex h-full w-full flex-col items-center p-4">
        <div className="flex flex-col items-center gap-4 mt-40">
          <SvgOcrFailed />

          <div className="text-center w-[18.1rem] h-[3.19rem] flex flex-col items-center justify-center">
            <div className="text-base font-bold text-red-700 mb-2 font-primary">
              {NAMESPACES.OCR_FAILED_TITLE}
            </div>
            <div className="text-base text-black">
              {NAMESPACES.OCR_FAILED_MESSAGE}
            </div>
          </div>
        </div>

        <div className="mt-8">
          <CustomButton
            isFilled
            color="others"
            className="!bg-blue-600 text-white hover:!bg-blue-700 w-44 h-8"
            onClick={() => handleProceedManually()}
          >
            {NAMESPACES.OCR_PROCEED_MANUALLY}
          </CustomButton>
        </div>
      </div>
    )
  } else if (ocrStatus === OCR_STATUS.SPLIT) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-5">
        <SvgOcrMultiFormsDetected />

        <div className="text-center text-lg font-medium leading-7 font-primary">
          {NAMESPACES.OCR_MULTIPLE_FORMS_TITLE}
          <br />
          {NAMESPACES.OCR_MULTIPLE_FORMS_MESSAGE}
        </div>
      </div>
    )
  } else if (ocrStatus === OCR_STATUS.CASE_CREATION_FAILED) {
    return (
      <OCRRejectedPage
        onProceedManually={() => handleProceedManually()}
        isCaseCreationFailed={true}
      />
    )
  } else {
    return (
      <div className="flex h-full min-h-0 w-full flex-col p-3">
        <div className="flex w-full shrink-0 items-center align-middle gap-2">
          <div className="bg-secondary-50 p-2 flex-1">
            <StepperBV
              steps={[
                {
                  label: NAMESPACES.OCR_REVIEW,
                  status: NAMESPACES.IN_PROGERSS_STEPPER,
                  order: 1,
                },
                {
                  label: NAMESPACES.OCR_INTAKE,
                  status: NAMESPACES.PENDING_STEPPER,
                  order: 2,
                },
                {
                  label: NAMESPACES.INTAKE_COMPLETE,
                  status: NAMESPACES.PENDING_STEPPER,
                  order: 3,
                },
              ]}
              currentStep={0}
            />
          </div>
          <CustomButton
            type="button"
            isFilled
            disabled={!nextButtonEnabled}
            onClick={submitFinalReviewAndCreateCase}
          >
            {NAMESPACES.NEXT_BUTTON}
          </CustomButton>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden pt-3">
          <TabSplits
            tabs={tabList}
            activeTabIndex={currStep}
            onTabChange={onTabChange}
            customScrollbarClass="custom-horizontal-scrollbar"
            tabPanelClassName="overflow-hidden"
            commonActionComponent={
              tabList?.[currStep]?.identifier ===
              OCR_TAB_IDENTIFIER.REJECTED ? null : (
                <div>
                  <CustomButton
                    onClick={handleSaveAndNext}
                    isFilled
                    disabled={!isSaveButtonEnabled}
                  >
                    {currStep === tabList.length - 1
                      ? NAMESPACES.SAVE_BUTTON
                      : NAMESPACES.OCR_SAVE_AND_NEXT}
                  </CustomButton>
                </div>
              )
            }
            tabsHeaderLeftComponent={
              <TabHeaderPageSummary
                acceptedCount={
                  (tabsMetaInfo?.pages?.length ?? 0) -
                  (tabsMetaInfo?.rejected_pages?.page_indexes?.length ?? 0)
                }
                rejectedCount={
                  tabsMetaInfo?.rejected_pages?.page_indexes?.length ?? 0
                }
              />
            }
          />
        </div>
      </div>
    )
  }
}

export default OCRReviewPage
